<script lang="ts" setup>
import ApplicationLogo from "@/components/ApplicationLogo.vue";
import NotificationList from "@/components/NotificationList.vue";
import { useSidebarStore } from "@/stores";
import { Link } from "@inertiajs/vue3";
import { LogOut, Settings, User } from "lucide-vue-next";
import { onClickOutside } from '@vueuse/core';
import { ref } from "vue";

const sidebar = ref(null)

onClickOutside(sidebar, () => {
  sidebarStore.closeMobile();
})

const sidebarStore = useSidebarStore();
</script>

<template>
  <div class="h-screen w-full bg-gray-100 dark:bg-gray-950">
    <Head :title="$page.props.title" />

    <NotificationList />

    <div class="flex overflow-hidden w-full h-screen">
      <Drawer
        class="hidden md:flex transition-all z-20 h-full flex-grow-1 flex-col overflow-x-hidden gap-4"
        :class="{
          'translate-x-0 w-56': sidebarStore.open,
          '-translate-x-full w-0': !sidebarStore.open,
        }"
      >
        <div class="h-16 border-b flex items-center justify-start px-4 sm:px-4">
          <Link :href="route('admin.dashboard')">
            <ApplicationLogo class="h-10" />
          </Link>
        </div>
        <ul
          class="flex flex-col w-full justify-start gap-2 p-1"
          v-for="menu in ($page.props?.menu as any) ?? []"
        >
          <li>
            <p class="text-xs uppercase px-2">{{ menu.title }}</p>
          </li>
          <Link
            v-bind="item"
            class="flex items-center gap-4 px-2 text-sm h-8 rounded hover:bg-gray-100"
            v-for="item in menu.children"
          >
            <Icon
              :name="item.icon"
              class="flex-shrink-0"
            />
            {{ item.title }}
          </Link>
        </ul>
      </Drawer>
      <Drawer
        ref="sidebar"
        class="flex flex-col z-40 md:hidden w-64 transition-transform p-2 fixed h-screen"
        :class="{
          'translate-x-0 w-56 p-2': sidebarStore.openMobile,
          '-translate-x-full w-0': !sidebarStore.openMobile,
        }"
      >
        <Link :href="route('admin.dashboard')">
          <ApplicationLogo class="h-10" />
        </Link>
        <ul
          class="flex flex-col w-full justify-start gap-2 p-1"
          v-for="menu in ($page.props?.menu as any) ?? []"
        >
          <li>
            <p class="text-xs uppercase">{{ menu.title }}</p>
          </li>
          <Link
            v-bind="item"
            class="flex items-center gap-4 text-sm h-8 p-2 rounded hover:bg-gray-100"
            v-for="item in menu.children"
          >
            <Icon
              :name="item.icon"
              class="flex-shrink-0"
            />
            {{ item.title }}
          </Link>
        </ul>
      </Drawer>
      <div class="h-full w-full overflow-y-auto overflow-x-hidden">
        <!-- Navbar -->
        <div class="sticky top-0 z-20 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 border-b">
          <div class="flex-1 px-4 flex justify-between">
            <div class="flex items-center justify-between w-full text-gray-900 dark:text-gray-100">
              <div class="flex items-center gap-2">
                <button @click="sidebarStore.toggle" class="hidden md:block">
                  <Icon
                    class="h-6 w-6"
                    name="PanelRightOpen"
                  />
                </button>
                <button @click="sidebarStore.toggleMobile" class="static md:hidden">
                  <Icon
                    class="h-6 w-6"
                    name="Menu"
                  />
                </button>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem v-for="(item, index) in $page.props.breadcrumbs">
                      <BreadcrumbSeparator v-if="index > 0" />
                      <BreadcrumbLink :as="Link" v-bind="item">
                        {{ item.title }}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <Button
                    variant="ghost"
                    class="flex flex-col gap-px items-start"
                  >
                    <span>{{ $page.props.auth.user.name }}</span>
                    <span class="text-gray-500">{{ $page.props.auth.user.email }}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent class="w-56">
                  <DropdownMenuGroup>
                    <Link :href="route('profile.edit')">
                      <DropdownMenuItem>
                        <User class="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </DropdownMenuItem>
                    </Link>
                    <Link :href="route('settings.edit')">
                      <DropdownMenuItem>
                        <Settings class="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </DropdownMenuItem>
                    </Link>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    :as="Link"
                    method="post"
                    :href="route('logout')"
                  >
                    <LogOut class="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <slot></slot>
      </div>
    </div>
  </div>
</template>
